import { isEmpty } from "ramda";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@/components/atoms";
import { DataTable, EmptyState, Pagination } from "@/components/molecules";
import { HowPricingWorksButton, HowPricingWorksModal, useContractPricingProfilesContext } from "@/components/organisms";
import { usePagination, useProfile, useSorting } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { pricingProfilesPageSelector } from "@/redux/slices/trip/selectors";
import { PricingProfile } from "@/redux/slices/trip/types";
import { DeletePricingProfileModal } from "./DeletePricingProfileModal";
import { generateColumns, generateData, getPaginationLabel } from "./helpers";
import { ContractPricingProfilesAction } from "./types";

export const ContractPricingProfilesTable = () => {
  const navigate = useNavigate();
  const { dateFormat } = useProfile();
  const { sorting, setSorting } = useSorting();
  const { current, size, total } = useAppSelector(pricingProfilesPageSelector);
  const { profiles } = useContractPricingProfilesContext();
  const [action, setAction] = useState<ContractPricingProfilesAction>(undefined);
  const [selectedProfile, setSelectedProfile] = useState<PricingProfile | undefined>(undefined);

  const handleAdd = () => navigate("add");

  const handleManage = (profile: PricingProfile) => navigate(profile.id);

  const handleDelete = (profile: PricingProfile) => {
    setSelectedProfile(profile);
    setAction("deletePricingProfile");
  };

  const data = useMemo(
    () => generateData(profiles, handleManage, handleDelete, dateFormat),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profiles]
  );

  const columns = useMemo(
    () => generateColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
      <div className="mb-4 flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">Contract Pricing Profiles</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Set up different pricing structures for different services or clients
          </Typography>
        </div>
        <div className="flex flex-col items-center gap-3 md:flex-row">
          <HowPricingWorksButton onClick={() => setAction("howPricingWorks")} className="w-full md:w-fit" />
          {!isEmpty(data) && (
            <div className="relative w-full md:w-fit">
              <Button variant="primary" size="sm" startIcon="Add" onClick={handleAdd} className="w-full md:w-fit">
                Add Pricing Profile
              </Button>
            </div>
          )}
        </div>
      </div>
      {isEmpty(data) ? (
        <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray">
          <EmptyState
            title="Nothing here… yet"
            description="You haven't added anything to this section. Get started by clicking the button below."
          >
            <div className="relative">
              <Button variant="primary" size="sm" startIcon="Add" onClick={handleAdd}>
                Add Pricing Profile
              </Button>
            </div>
          </EmptyState>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data}
            sorting={sorting}
            setSorting={setSorting}
            className="w-full overflow-x-auto rounded-b-none bg-white [&_table]:table-auto"
          />
          <div className="flex w-full items-center justify-between rounded-b-lg bg-neutral-surface-gray p-2 ring-1 ring-neutral-mid-gray">
            <Typography variant="small-regular" className="text-neutral-silver-gray">
              {getPaginationLabel(current, size, total)}
            </Typography>
          </div>
          <ContractPricingProfilesPagination />
        </>
      )}
      <HowPricingWorksModal open={action === "howPricingWorks"} onClose={() => setAction(undefined)} />
      <DeletePricingProfileModal
        open={action === "deletePricingProfile"}
        onClose={() => {
          setAction(undefined);
          setSelectedProfile(undefined);
        }}
        selectedProfile={selectedProfile}
      />
    </div>
  );
};

const ContractPricingProfilesPagination = () => {
  const { current, size, total, count } = useAppSelector(pricingProfilesPageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("contract-pricing-profiles");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};
